import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Loader, X } from 'lucide-react';
import { Link, useParams } from "react-router-dom";
import { useLanguage } from "../utils/LanguageContext";
import { useBotProtection, BotProtectionFields } from '../utils/useBotProtection';
import { Helmet } from 'react-helmet-async';

const API_BASE_URL = 'https://texla.uz/api/v1';

// Tilga qarab matn qaytarish
const getLocalizedText = (obj, language) => {
    if (!obj) return '';
    const key = `title_${language}`;
    return obj[key] || obj.title_uz || obj.title || '';
};

// Tilga qarab product nomi qaytarish
const getLocalizedProductName = (product, language) => {
    if (!product) return '';
    const key = `name_${language}`;
    return product[key] || product.name_uz || product.name || '';
};

// Tilga qarab description qaytarish
const getLocalizedDescription = (product, language) => {
    if (!product) return '';
    const key = `description_${language}`;
    return product[key] || product.description_uz || product.description || '';
};

// SEO title olish - agar bo'sh bo'lsa, mahsulot nomini ishlatish
const getSeoTitle = (product, language) => {
    if (!product) return '';
    const seoKey = `seo_title_${language}`;
    const seoTitle = product[seoKey];
    if (seoTitle) return seoTitle;
    return getLocalizedProductName(product, language);
};

// SEO description olish - agar bo'sh bo'lsa, mahsulot descriptionini ishlatish
const getSeoDescription = (product, language) => {
    if (!product) return '';
    const seoKey = `seo_description_${language}`;
    const seoDesc = product[seoKey];
    if (seoDesc) return seoDesc;

    // Description'dan birinchi 160 ta belgini olish
    const desc = getLocalizedDescription(product, language);
    const plainText = desc.replace(/<[^>]*>/g, '').substring(0, 160);
    return plainText.length === 160 ? plainText + '...' : plainText;
};

// SEO keywords olish
const getSeoKeywords = (product) => {
    if (!product) return '';
    return product.seo_keywords || '';
};

const ProductDetailPage = () => {
    const { id } = useParams();
    const { language, t } = useLanguage();

    const [product, setProduct] = useState(null);
    const [recentlyViewed, setRecentlyViewed] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [formData, setFormData] = useState({ name: '', phone: '' });
    const bot = useBotProtection();
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        loadProduct();
        loadRecentlyViewed();
    }, [id]);

    const loadProduct = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/products/${id}/`);
            const data = await response.json();
            setProduct(data);

            // Add to recently viewed
            const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
            const updated = [data, ...viewed.filter(p => p.id !== data.id)].slice(0, 10);
            localStorage.setItem('recentlyViewed', JSON.stringify(updated));
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const loadRecentlyViewed = () => {
        try {
            const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
            const filtered = viewed.filter(p => p.id != id);
            setRecentlyViewed(filtered.slice(0, 4));
        } catch (error) {
        }
    };

    const trackConversion = () => {
        if (typeof window.gtag === 'function') {
            window.gtag('event', 'conversion', {
                send_to: 'AW-17874935826/_4UICJr_3eIbEJLAt8tC',
                value: 1.0,
                currency: 'USD',
                transaction_id: ''
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.phone || !product) return;
        if (!bot.validate()) return;

        try {
            await fetch(`${API_BASE_URL}/zayavka/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    product: product.id,
                    name: formData.name,
                    phone: formData.phone
                })
            });

            trackConversion();

            setSubmitted(true);
            setTimeout(() => {
                setShowModal(false);
                setSubmitted(false);
                setFormData({ name: '', phone: '' });
            }, 3000);
        } catch (error) {
            console.error('Error submitting order:', error);
            alert(t('modal.error'));
        }
    };

    const nextImage = () => {
        if (currentImages.length > 1) {
            setCurrentImageIndex((prev) =>
                prev === currentImages.length - 1 ? 0 : prev + 1
            );
        }
    };

    const prevImage = () => {
        if (currentImages.length > 1) {
            setCurrentImageIndex((prev) =>
                prev === 0 ? currentImages.length - 1 : prev - 1
            );
        }
    };

    const getImageUrl = (image) => {
        if (typeof image === 'string') return image;
        if (image?.image) return image.image;
        return 'https://via.placeholder.com/400';
    };

    const getAllImages = () => {
        const images = [];
        if (product?.main_image) {
            images.push(product.main_image);
        }
        if (product?.other_images && Array.isArray(product.other_images)) {
            images.push(...product.other_images.map(img => getImageUrl(img)));
        }
        return images.length > 0 ? images : ['https://via.placeholder.com/400'];
    };

    const currentImages = getAllImages();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <Loader className="animate-spin text-gray-400" size={48} />
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="text-center">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t('product.productNotFound')}</h2>
                    <Link
                        to={`/shop/`}
                        className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition"
                    >
                        {t('product.backToCatalog')}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white py-8">
            <Helmet>
                <title>{getSeoTitle(product, language)} | TexLab</title>
                <meta name="description" content={getSeoDescription(product, language)} />
                <meta name="keywords" content={getSeoKeywords(product)} />
                <meta property="og:title" content={getSeoTitle(product, language)} />
                <meta property="og:description" content={getSeoDescription(product, language)} />
                <meta property="og:image" content={product?.main_image || 'https://via.placeholder.com/400'} />
            </Helmet>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Breadcrumb */}
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
                    <Link to={"/"} className="hover:text-gray-900">{t('breadcrumb.home')}</Link>
                    <span>/</span>
                    <Link to={"/shop"} className="hover:text-gray-900">{t('breadcrumb.catalog')}</Link>
                    <span>/</span>
                    <span className="text-gray-900">{getLocalizedProductName(product, language)}</span>
                </div>

                {/* Product Details */}
                <div className="mb-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Image Gallery */}
                        <div>
                            <div className="relative bg-gray-50 rounded-lg overflow-hidden mb-4" style={{ paddingTop: '100%' }}>
                                <img
                                    src={currentImages[currentImageIndex]}
                                    alt={getLocalizedProductName(product, language)}
                                    className="absolute inset-0 w-full h-full object-contain p-4"
                                />

                                {currentImages.length > 1 && (
                                    <>
                                        <button
                                            onClick={prevImage}
                                            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-100 p-2 rounded-full shadow transition"
                                        >
                                            <ChevronLeft size={24} className="text-gray-900" />
                                        </button>
                                        <button
                                            onClick={nextImage}
                                            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-100 p-2 rounded-full shadow transition"
                                        >
                                            <ChevronRight size={24} className="text-gray-900" />
                                        </button>
                                    </>
                                )}
                            </div>

                            {/* Thumbnail Gallery */}
                            {currentImages.length > 1 && (
                                <div className="grid grid-cols-4 gap-4">
                                    {currentImages.map((image, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentImageIndex(index)}
                                            className={`relative bg-gray-50 rounded-lg overflow-hidden border-2 transition ${
                                                currentImageIndex === index
                                                    ? 'border-gray-900'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                            style={{ paddingTop: '100%' }}
                                        >
                                            <img
                                                src={image}
                                                alt={`${getLocalizedProductName(product, language)} ${index + 1}`}
                                                className="absolute inset-0 w-full h-full object-cover"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Product Info */}
                        <div>
                            <div className="mb-6">
                                <p className="text-sm text-gray-600 font-medium mb-2">{product.code}</p>
                                <h1 className="text-3xl font-semibold text-gray-900 mb-4">{getLocalizedProductName(product, language)}</h1>

                                <div className="flex flex-wrap gap-6 mb-4">
                                    {product.brands && product.brands.length > 0 && (
                                        <div>
                                            <span className="text-sm text-gray-600">{t('product.brand')}: </span>
                                            <span className="font-medium text-gray-900">{getLocalizedText(product.brands[0], language)}</span>
                                        </div>
                                    )}
                                    {product.categories && product.categories.length > 0 && (
                                        <div>
                                            <span className="text-sm text-gray-600">{t('product.category')}: </span>
                                            <span className="font-medium text-gray-900">{getLocalizedText(product.categories[0], language)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Request Button */}
                            <button
                                onClick={() => { setShowModal(true); bot.reset(); }}
                                className="mt-4 w-full bg-orange-600 hover:bg-orange-700 text-white py-4 rounded-lg font-semibold text-lg transition"
                            >
                                {t('home.request')}
                            </button>
                        </div>

                    </div>
                    {/* Description */}
                    {getLocalizedDescription(product, language) && (
                        <div className="mb-8 mt-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('product.description')}</h3>
                            <div className="text-gray-600 leading-relaxed prose prose-sm max-w-none">
                                <div dangerouslySetInnerHTML={{ __html: getLocalizedDescription(product, language) }} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Recently Viewed */}
                {recentlyViewed.length > 0 && (
                    <section className="mb-12">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-6">{t('product.recentlyViewed')}</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {recentlyViewed.map((item) => (
                                <Link
                                    key={item.id}
                                    to={`/product/${item.id}`}
                                    className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition"
                                >
                                    <div className="relative aspect-square bg-gray-50">
                                        <img
                                            src={item.main_image || 'https://via.placeholder.com/400'}
                                            alt={getLocalizedProductName(item, language)}
                                            className="w-full h-full object-contain p-4"
                                        />
                                    </div>
                                    <div className="p-4">
                                        <span className="inline-block text-xs font-medium text-gray-600 bg-gray-50 px-2.5 py-1 rounded mb-2">
                                            {item.code}
                                        </span>
                                        <h3 className="font-medium text-gray-900 mb-2 line-clamp-2 text-sm">
                                            {getLocalizedProductName(item, language)}
                                        </h3>
                                        {item.brands && item.brands.length > 0 && (
                                            <p className="text-xs text-gray-500">
                                                {getLocalizedText(item.brands[0], language)}
                                            </p>
                                        )}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </div>

            {/* Inquiry Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
                        <button
                            onClick={() => setShowModal(false)}
                            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                        >
                            <X size={24} />
                        </button>

                        {!submitted ? (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('modal.title')}</h3>
                                <p className="text-xs text-gray-600 font-medium mb-1">{product.code}</p>
                                <p className="text-gray-800 mb-6 font-medium text-sm line-clamp-2">{getLocalizedProductName(product, language)}</p>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('modal.fullName')} {t('modal.required')}
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300 focus:border-transparent outline-none text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('modal.phone')} {t('modal.required')}
                                        </label>
                                        <input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300 focus:border-transparent outline-none text-sm"
                                        />
                                    </div>

                                    <BotProtectionFields bot={bot} />

                                    <button
                                        onClick={handleSubmit}
                                        className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 rounded-lg transition"
                                    >
                                        {t('modal.send')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                    {t('modal.successTitle')}
                                </h4>
                                <p className="text-sm text-gray-600">{t('modal.successMessage')}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductDetailPage;